import { Component, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { SupplierLogoService } from 'src/app/data/service/supplier-logo.service';

/**
 * SD-1240. A supplier's avatar: the master contact's logo when there is one, otherwise the
 * initials badge every supplier has always had. The host element takes the page's own classes
 * (`sp-avatar`, `sc-avatar`, the `hexColorClass` colour), so each page keeps its sizing and
 * palette; when a logo shows, the box goes white. A logo that fails to load falls back to initials.
 *
 *   <app-supplier-avatar class="up-avatar sp-avatar" [ngClass]="row.hexColorClass"
 *                        [name]="row.companyName" [logoUrl]="row.logoUrl"></app-supplier-avatar>
 *
 * Without a `logoUrl`, give it the supplier and it finds the logo itself (SupplierLogoService):
 * `supplierId` where the row has one, `supplierName` where it has only the name. Only those two
 * look a logo up, so a person's or a client's avatar never picks up a supplier's logo.
 * Content placed inside replaces the computed initials, so a page can keep its own markup:
 *
 *   <app-supplier-avatar class="sup_img_box {{row.hexColorClass}}" [supplierName]="row.supplierName">
 *     <span>{{row.shortCode}}</span>
 *   </app-supplier-avatar>
 */
@Component({
  selector: 'app-supplier-avatar',
  templateUrl: './supplier-avatar.component.html',
  styleUrls: ['./supplier-avatar.component.scss']
})
export class SupplierAvatarComponent implements OnChanges, OnDestroy {
  @Input() name: string;
  @Input() logoUrl: string;
  @Input() supplierId: string;
  @Input() supplierName: string;
  /** Whose suppliers to look the logo up in; the selected organisation when not given (a practice's client rows pass the client). */
  @Input() organizationId: string;
  /** A sender's email address: with no supplier found by id or name, the contact whose website is on its domain. */
  @Input() email: string;

  failed = false;
  private foundUrl: string = null;
  private lookup: Subscription;

  private changes: Subscription;

  constructor(private supplierLogos: SupplierLogoService) {
    // A website saved or contacts merged: a badge that looks its logo up looks again.
    this.changes = this.supplierLogos.changed$.subscribe(() => {
      if (this.supplierId || this.supplierName) { this.findLogo(); }
    });
  }

  get imageUrl(): string { return this.logoUrl || this.foundUrl; }

  @HostBinding('class.sup-avatar--logo') get showImage(): boolean { return !!this.imageUrl && !this.failed; }
  @HostBinding('attr.aria-hidden') ariaHidden = 'true';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.logoUrl) this.failed = false;
    if (changes.supplierId || changes.supplierName || changes.organizationId || changes.email) this.findLogo();
  }

  ngOnDestroy(): void {
    this.lookup?.unsubscribe();
    this.changes?.unsubscribe();
  }

  get initials(): string {
    const v = (this.name || this.supplierName || '').trim();
    if (!v) return '#';
    return v.split(' ').filter(w => w.length > 0).map(w => w[0]).join('').slice(0, 3).toUpperCase();
  }

  private findLogo(): void {
    this.lookup?.unsubscribe();
    this.foundUrl = null;
    this.failed = false;
    if (!this.supplierId && !this.supplierName && !this.email) return;
    this.lookup = this.supplierLogos.logoFor(this.supplierId, this.supplierName, this.organizationId, this.email)
      .subscribe(url => this.foundUrl = url);
  }
}
